<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContent v-if="data">
      <template #top-bar>
        <div class="flex grow items-center gap-2">
          <BaseMemberAvatar size="56" :member="data.member" class="mr-4 self-baseline" />
          <h1 class="text-display-small">{{ memberFullName(data.member) }}</h1>

          <div class="ml-auto flex items-center">
            <BaseChip v-if="data.status != CoachingStatus.Active" type="assist">
              {{ $t('coaching.status.' + data.status + '.title') }}
            </BaseChip>
          </div>
          <BaseMenu v-if="menuItems.length" :items="menuItems" v-slot="{ open }">
            <BaseIconButton color="standard" icon="more_vert" :force-pressed="open" />
          </BaseMenu>
        </div>
      </template>
      <div class="flex flex-col pb-10">
        <CoacheePendingState v-if="data.status === CoachingStatus.Pending" />
        <CoacheeRejectedState
          v-if="data.status === CoachingStatus.Rejected"
          :member="data.member"
          @resend="resendInvitationAndReload"
          @withdraw="deleteCoachingAndRoute"
        />
        <div v-if="data.status == CoachingStatus.Active">
          <BaseTabGroup :tab-items="tabItems">
            <BaseTabBar :tab-items="tabItems" class="mb-4 border-b" />
            <TabPanels>
              <TabPanel
                v-if="currentOrganization.modules.includes(ApplicationModule.SUPERVISED_COACHING)"
              >
                <CoacheeProfilePage />
              </TabPanel>
              <TabPanel v-if="currentOrganization.modules.includes(ApplicationModule.DIALOG)">
                <CoacheeDialogPage />
              </TabPanel>
              <TabPanel v-if="currentOrganization.modules.includes(ApplicationModule.COACHING)">
                <CoacheeTodosPage />
              </TabPanel>
              <TabPanel v-if="currentOrganization.modules.includes(ApplicationModule.DIALOG)">
                <CoacheeObjectivesPage />
              </TabPanel>
            </TabPanels>
          </BaseTabGroup>
        </div>

        <BaseDialog
          type="basic"
          :is-open="isOpenDeleteDialog"
          :title="$t('coaching.terminateDialog.title')"
          :description="
            $t('coaching.terminateDialog.description', {
              firstName: memberShortName(data.member),
            })
          "
          @close="closeDeleteDialog"
        >
          <template #confirm>
            <BaseTextButton @click="remove">
              {{ $t('coaching.terminateDialog.button') }}
            </BaseTextButton>
          </template>
          <template #dismiss>
            <BaseTextButton @click="closeDeleteDialog">
              {{ $t('common.cancel') }}
            </BaseTextButton>
          </template>
        </BaseDialog>
      </div>
    </SideBarLayoutContent>
  </LoadingWrapper>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useEventBus } from '@vueuse/core'
  import SideBarLayoutContent from '@/core/components/layouts/SideBarLayoutContent.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { EventKey } from '@/core/events/event-key'
  import { CoachingStatus } from '@/modules/dashboard/composables/useCoachingInvitations'
  import { useCoachee } from '../composables/useCoachee'
  import { useMutateCoachee } from '../composables/useMutateCoachee'
  import CoacheePendingState from '../components/CoacheePendingState.vue'
  import CoacheeRejectedState from '../components/CoacheeRejectedState.vue'
  import { TabPanel, TabPanels } from '@headlessui/vue'
  import BaseTabBar from '@/core/components/base/tab-bar/BaseTabBar.vue'
  import BaseTabGroup from '@/core/components/base/tab-bar/BaseTabGroup.vue'
  import { useOrganizationStore } from '@/stores/organization.store'
  import { ApplicationModule } from '@/core/models/organization'
  import CoacheeDialogPage from './CoacheeDialogPage.vue'
  import CoacheeObjectivesPage from './CoacheeObjectivesPage.vue'
  import CoacheeTodosPage from './CoacheeTodosPage.vue'
  import CoacheeProfilePage from './CoacheeProfilePage.vue'
  import { memberFullName, memberShortName } from '@/core/models/member/member-name'

  const router = useRouter()
  const { t } = useI18n()
  const bus = useEventBus(EventKey.dialogUpdated)

  const { data, status } = useCoachee()
  const { deleteCoaching, resendCoachingInvitation } = useMutateCoachee()

  const {
    openDialog: openDeleteDialog,
    isOpen: isOpenDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()

  const deleteCoachingAndRoute = async () => {
    if (!data.value) return
    await deleteCoaching(data.value.member.id)
    router.push({ name: 'coachees' })
  }

  const resendInvitationAndReload = async () => {
    if (data.value) {
      await resendCoachingInvitation(data.value.member.id)
      bus.emit()
    }
  }

  const menuItems = computed(() => {
    const items = []
    items.push({
      id: 'delete',
      leadingIcon: 'person_remove',
      title: t('coaching.terminateDialog.button'),
      destructive: true,
      callback: () => openDeleteDialog(),
    })
    return items
  })

  const remove = async () => {
    if (!data.value) return
    await deleteCoaching(data.value.member.id)
    closeDeleteDialog()
  }

  const { currentOrganization } = useOrganizationStore()

  const tabItems = computed(() => {
    const tabs: {
      id: string
      title: string
      icon?: string
      img?: string
      badgeCount?: number
    }[] = []
    if (currentOrganization.modules.includes(ApplicationModule.SUPERVISED_COACHING)) {
      tabs.push({
        id: 'coachee-profile',
        title: t('coaching.coacheeProfile.title'),
        icon: 'widgets',
      })
    }
    if (currentOrganization.modules.includes(ApplicationModule.DIALOG)) {
      tabs.push({
        id: 'coachee-dialog',
        title: t('coaching.coacheeDialog.title'),
        icon: 'forum',
      })
    }
    if (currentOrganization.modules.includes(ApplicationModule.COACHING)) {
      tabs.push({
        id: 'coachee-todos',
        title: t('coaching.coacheeTodos.title'),
        icon: 'check_circle',
      })
    }
    if (currentOrganization.modules.includes(ApplicationModule.DIALOG)) {
      tabs.push({
        id: 'coachee-objectives',
        title: t('coaching.coacheeObjectives.title'),
        icon: 'target',
      })
    }
    return tabs
  })
</script>
